import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Logo from './Logo';

const EmailConfirmation: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      try {
        setLoading(true);
        setError('');

        // Check if we have tokens from Supabase verification
        // Supabase may provide tokens either as query params or in the URL hash fragment
        const queryAccessToken = searchParams.get('access_token');
        const queryRefreshToken = searchParams.get('refresh_token');

        const hash = typeof window !== 'undefined' ? window.location.hash : '';
        const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
        const hashAccessToken = hashParams.get('access_token');
        const hashRefreshToken = hashParams.get('refresh_token');

        // Get token and type from query params
        let token = searchParams.get('token');
        let type = searchParams.get('type');

        // If token is not in query params, try to extract from the full URL
        // This handles cases where Supabase's /auth/v1/verify redirects without preserving query params
        if (!token && typeof window !== 'undefined') {
          const fullUrl = window.location.href;
          const urlMatch = fullUrl.match(/[?&]token=([^&]+)/);
          if (urlMatch) {
            token = urlMatch[1];
          }
          if (!type) {
            const typeMatch = fullUrl.match(/[?&]type=([^&]+)/);
            if (typeMatch) {
              type = typeMatch[1];
            }
          }
        }

        // If still no token, try to extract from document.referrer (might contain the original verification URL)
        if (!token && typeof window !== 'undefined' && document.referrer) {
          const referrerMatch = document.referrer.match(/[?&]token=([^&]+)/);
          if (referrerMatch) {
            token = referrerMatch[1];
          }
          if (!type && document.referrer.includes('type=')) {
            const typeMatch = document.referrer.match(/[?&]type=([^&]+)/);
            if (typeMatch) {
              type = typeMatch[1];
            }
          }
        }

        // Also check if there's a token in the URL when accessed via Supabase's verify endpoint
        // Sometimes the token might be preserved if we can access the original request
        if (!token && typeof window !== 'undefined') {
          // Check sessionStorage for a stored token (in case we need to handle it across redirects)
          const storedToken = sessionStorage.getItem('email_confirm_token');
          const storedType = sessionStorage.getItem('email_confirm_type');
          if (storedToken) {
            token = storedToken;
            sessionStorage.removeItem('email_confirm_token');
          }
          if (storedType) {
            type = storedType;
            sessionStorage.removeItem('email_confirm_type');
          }
        }

        // Case 1: We have access/refresh tokens (from query or hash) → set the session
        const accessToken = queryAccessToken || hashAccessToken;
        const refreshToken = queryRefreshToken || hashRefreshToken;
        
        if (accessToken && refreshToken) {
          const { error: sessionError } = await supabase.auth.setSession({ 
            access_token: accessToken, 
            refresh_token: refreshToken 
          });
          
          if (sessionError) {
            setError('Invalid or expired confirmation link. The link may have already been used or may have expired.');
            setLoading(false);
            return;
          }
          
          // Session is now set; email is confirmed
          setSuccess(true);
          setLoading(false);
          return;
        } 
        
        // Case 2: We have a token with type 'signup' or 'email' - verify using verifyOtp
        if (token && (type === 'signup' || type === 'email')) {
          // Decode the token if it's URL encoded
          const decodedToken = decodeURIComponent(token);
          
          const { data, error: verifyError } = await supabase.auth.verifyOtp({
            token_hash: decodedToken,
            type: type as 'signup' | 'email'
          });

          if (verifyError) {
            // Provide more detailed error message
            let errorMessage = 'Invalid or expired confirmation link. The link may have already been used or may have expired.';
            if (verifyError.message) {
              if (verifyError.message.includes('already been used') || verifyError.message.includes('already used')) {
                errorMessage = 'This confirmation link has already been used. If you have already confirmed your email, please try logging in instead.';
              } else if (verifyError.message.includes('expired') || verifyError.message.includes('expiration')) {
                errorMessage = 'This confirmation link has expired. Please request a new confirmation email from the login page.';
              } else if (verifyError.message.includes('invalid') || verifyError.message.includes('not found')) {
                errorMessage = 'This confirmation link is invalid or has already been used. Please request a new confirmation email.';
              } else {
                errorMessage = verifyError.message;
              }
            }
            setError(errorMessage);
            setLoading(false);
            return;
          }

          // If verification successful, the user session is now set and email is confirmed
          setSuccess(true);
          setLoading(false);
          return;
        }
        
        // Case 3: Check if we're being redirected from Supabase's /auth/v1/verify endpoint
        // Sometimes Supabase processes the verification and redirects with an error or success indicator
        const errorParam = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');
        
        if (errorParam) {
          let errorMessage = 'Email confirmation failed.';
          if (errorDescription) {
            errorMessage = decodeURIComponent(errorDescription);
          } else if (errorParam === 'token_not_found') {
            errorMessage = 'Invalid confirmation link. The token was not found.';
          } else if (errorParam === 'token_expired') {
            errorMessage = 'This confirmation link has expired. Please request a new confirmation email.';
          }
          setError(errorMessage);
          setLoading(false);
          return;
        }

        // Case 4: Check if we're on confirm-email page but came from Supabase verify endpoint
        // This means Supabase processed the token server-side but didn't pass it through
        // This typically happens when the token was already used or expired
        if (!token && !accessToken && typeof window !== 'undefined') {
          // Check if referrer was from Supabase verify endpoint
          const referrer = document.referrer;
          if (referrer && referrer.includes('/auth/v1/verify')) {
            setError('This confirmation link may have already been used or has expired. If you have already confirmed your email, please try logging in. Otherwise, please request a new confirmation email.');
            setLoading(false);
            return;
          }
        }
        
        // No valid tokens found
        setError('Invalid confirmation link. Please check your email for the correct confirmation link. If this problem persists, the link may have expired - please request a new confirmation email.');
        setLoading(false);
      } catch (error: any) {
        setError(error?.message || 'An error occurred while confirming your email. Please try again or contact support.');
        setLoading(false);
      }
    };

    handleEmailConfirmation();
  }, [searchParams, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <Logo />
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Confirming Your Email
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Please wait while we confirm your email address...
            </p>
            <div className="mt-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <Logo />
            </div>
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Email Confirmed Successfully!
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Your email address has been successfully confirmed. Your account is now active!
            </p>
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 font-medium mb-2">
                📱 Next Steps:
              </p>
              <p className="text-sm text-blue-700">
                You can now return to the Deal Once mobile app and log in with your credentials.
              </p>
            </div>
            <p className="mt-4 text-xs text-gray-500">
              You can safely close this window.
            </p>
            <p className="mt-6 text-xs text-gray-400">
              © 2025 Deal Once. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <Logo />
          </div>
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
            <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Email Confirmation Failed
          </h2>
          <div className="mt-4">
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          </div>
          <p className="mt-6 text-xs text-gray-500">
            If you continue to experience issues, please contact support at <a href="mailto:support@dealonce.com" className="text-indigo-600 hover:text-indigo-800 underline">support@dealonce.com</a> or request a new confirmation email.
          </p>
          <p className="mt-6 text-xs text-gray-400">
            © 2025 Deal Once. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailConfirmation;

